import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { FaCheck } from 'react-icons/fa';
import './TeacherSignupWizard.css';

// Step components
import Step1About from './components/wizard/Step1About';
import Step2Photo from './components/wizard/Step2Photo';
import Step3Experience from './components/wizard/Step3Experience';
import Step4Description from './components/wizard/Step4Description';
import Step5Video from './components/wizard/Step5Video';
import Step6Availability from './components/wizard/Step6Availability';
import Step7Pricing from './components/wizard/Step7Pricing';
import Step8Places from './components/wizard/Step8Places';
import Step10Review from './components/wizard/Step10Review';
import WizardCompletion from './components/wizard/WizardCompletion';

const STEP_LABELS = {
  EN: ['About', 'Photo', 'Experience', 'Description', 'Video', 'Availability', 'Places', 'Pricing', 'Review'],
  AR: ['حول', 'الصورة', 'الخبرة', 'الوصف', 'الفيديو', 'التوفر', 'الأماكن', 'التسعير', 'المراجعة']
};

// Wizard Translations - Export for use in admin page
export const wizardTranslations = {
  EN: {
    // Common buttons
    back: 'Back',
    saveAndContinue: 'Save and continue',
    finish: 'Finish',
    skipForNow: 'Skip for now',
    // Step titles
    about: 'About',
    photo: 'Profile Photo',
    experience: 'Experience',
    description: 'Description',
    video: 'Intro Video (Optional)',
    availability: 'Availability',
    pricing: 'Pricing',
    places: 'Places',
    form: 'Optional Questions',
    // Step subtitles
    aboutSubtitle: 'Tell us a bit about yourself. We\'ll use this information to help students find you.',
    photoSubtitle: 'Upload a clear photo of your face. It helps students trust you.',
    experienceSubtitle: 'Share your teaching or professional experience. This helps students understand your background.',
    descriptionSubtitle: 'Write a short bio about yourself. This helps students get to know you.',
    videoSubtitle: 'A short intro video helps students choose you faster. Keep it 30-60 seconds.',
    availabilitySubtitle: 'Set your weekly availability schedule. Students will see when you\'re available.',
    pricingSubtitle: 'Set your hourly rates. You can edit pricing later from your profile.',
    placesSubtitle: 'Choose how you prefer to deliver lessons and add locations for in-person teaching.',
    formSubtitle: 'These answers may help you get more bookings. All fields are optional.',
    // Step 1 - About
    fullName: 'Full Name',
    emailAddress: 'Email Address',
    phoneNumber: 'Phone Number',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    subjectYouTeach: 'Subject You Teach',
    languagesYouSpeak: 'Languages You Speak',
    addLanguage: 'Add Language',
    gradesYouTeach: 'Grade Levels You Teach',
    confirmOver18: 'I confirm I am over 18 years old',
    emailFromAccount: 'Email from your account',
    // Step 2 - Photo
    uploadPhoto: 'Upload Photo',
    clickToUpload: 'Click to upload photo',
    photoPreview: 'Photo preview will appear here',
    removeChange: 'Remove / Change',
    // Step 3 - Experience
    noExperience: 'I don\'t have any experience',
    jobTitle: 'Job Title',
    organization: 'Organization / Place',
    currentlyWorkHere: 'I currently work here',
    from: 'From',
    to: 'To',
    month: 'Month',
    year: 'Year',
    addMore: 'Add More',
    // Step 4 - Description
    shortBio: 'Short Bio',
    generateWithAI: 'Generate with AI',
    generating: 'Generating...',
    words: 'words',
    // Step 5 - Video
    chooseMethod: 'Choose Method',
    pasteVideoLink: 'Paste Video Link',
    uploadVideo: 'Upload Video',
    videoLink: 'Video Link (YouTube or Vimeo)',
    uploadVideoFile: 'Upload Video File (mp4, max 50MB, 60 seconds)',
    clickToUploadVideo: 'Click to upload video',
    tipsForVideo: 'Tips for a great intro video:',
    // Step 6 - Availability
    setAvailability: 'Set your weekly availability schedule. Students will see when you\'re available.',
    addAnotherTimeslot: 'Add another timeslot',
    // Step 7 - Pricing
    perHourPrivate: 'Per hour (private)',
    perHourGroup: 'Per hour (group)',
    bankingInfo: 'Banking Information',
    iban: 'IBAN',
    bankName: 'Bank Name',
    accountHolderName: 'Account Holder Name',
    forReceivingPayments: 'For receiving payments (optional)',
    // Step 8 - Places
    howDeliverLessons: 'How do you prefer to deliver lessons?',
    onlineOnly: 'Online Only',
    onlineOnlyDesc: 'I will only teach online (Zoom, Google Meet, etc.)',
    faceToFaceOnly: 'Face-to-Face Only',
    faceToFaceOnlyDesc: 'I will only teach in-person (at home, library, etc.)',
    both: 'Both',
    bothDesc: 'I can teach both online or in-person (student chooses)',
    preferredLocations: 'Preferred Teaching Locations',
    addLocation: 'Add Location',
    // Step 9 - Form
    interestedInCorporate: 'Are you interested in teaching company employees?',
    corporateTrainingHelp: 'If we make a contract with a company, would you be willing to teach their employees in your field?',
    preferredCompanySize: 'What size companies would you prefer to work with?',
    companySizeHelp: 'Select the company sizes you are comfortable teaching',
    trainingFormat: 'What training format do you prefer?',
    trainingFormatHelp: 'Select the training formats you are comfortable with for corporate training',
    corporateAvailability: 'When are you available for corporate training?',
    corporateAvailabilityHelp: 'Select your preferred times for conducting corporate training sessions',
    helpMatchStudents: 'Help us match you with the right students',
    howPreferToTeach: 'How do you prefer to teach?',
    whatLanguageLessons: 'What language will you use during lessons?',
    whenTypicallyAvailable: 'When are you typically available?',
    // Errors
    required: 'is required',
    validEmail: 'Please enter a valid email address',
    atLeastOneLanguage: 'At least one language is required',
    mustConfirmOver18: 'You must confirm you are over 18',
    pleaseUploadPhoto: 'Please upload a profile photo',
    pleaseWriteBio: 'Please write a short bio',
    // Review
    reviewYourProfile: 'Review Your Profile',
    reviewSubtitle: 'Please review all your information before submitting. You can edit any section by clicking the Edit button.',
    submitForReview: 'Submit for Review',
    // Review Section Titles
    information: 'INFORMATION',
    aboutMe: 'ABOUT ME',
    gradeLevels: 'GRADE LEVELS',
    languages: 'LANGUAGES',
    video: 'VIDEO',
    subjects: 'SUBJECTS',
    experience: 'EXPERIENCE',
    pricing: 'PRICING',
    availability: 'AVAILABILITY',
    teachingLocations: 'TEACHING LOCATIONS',
    bankingInformation: 'BANKING INFORMATION',
    preferredLocations: 'Preferred Locations:',
    location: 'Location',
    // Completion
    submissionReceived: 'Submission Received!',
    thankYouMessage: 'Thank you for completing your teacher profile submission.',
    emailNotification: 'The Taealam team will see your request in 24 hours and will answer you in the same time.',
    goToDashboard: 'Go to Dashboard',
  },
  AR: {
    // Common buttons
    back: 'رجوع',
    saveAndContinue: 'حفظ والمتابعة',
    finish: 'إنهاء',
    skipForNow: 'تخطي الآن',
    // Step titles
    about: 'حول',
    photo: 'صورة الملف الشخصي',
    experience: 'الخبرة',
    description: 'الوصف',
    video: 'فيديو التعريف (اختياري)',
    availability: 'التوفر',
    pricing: 'التسعير',
    places: 'الأماكن',
    form: 'أسئلة اختيارية',
    // Step subtitles
    aboutSubtitle: 'أخبرنا قليلاً عن نفسك. سنستخدم هذه المعلومات لمساعدة الطلاب على العثور عليك.',
    photoSubtitle: 'قم بتحميل صورة واضحة لوجهك. هذا يساعد الطلاب على الوثوق بك.',
    experienceSubtitle: 'شارك خبرتك التدريسية أو المهنية. هذا يساعد الطلاب على فهم خلفيتك.',
    descriptionSubtitle: 'اكتب نبذة قصيرة عن نفسك. هذا يساعد الطلاب على التعرف عليك.',
    videoSubtitle: 'يساعد فيديو تعريف قصير الطلاب على اختيارك بشكل أسرع. اجعله 30-60 ثانية.',
    availabilitySubtitle: 'حدد جدول التوفر الأسبوعي الخاص بك. سيرى الطلاب متى تكون متاحاً.',
    pricingSubtitle: 'حدد أسعارك بالساعة. يمكنك تعديل الأسعار لاحقاً من ملفك الشخصي.',
    placesSubtitle: 'اختر كيف تفضل تقديم الدروس وأضف المواقع للتدريس وجهاً لوجه.',
    formSubtitle: 'قد تساعدك هذه الإجابات في الحصول على المزيد من الحجوزات. جميع الحقول اختيارية.',
    // Step 1 - About
    fullName: 'الاسم الكامل',
    emailAddress: 'عنوان البريد الإلكتروني',
    phoneNumber: 'رقم الهاتف',
    dateOfBirth: 'تاريخ الميلاد',
    gender: 'الجنس',
    subjectYouTeach: 'المادة التي تدرسها',
    languagesYouSpeak: 'اللغات التي تتحدثها',
    addLanguage: 'إضافة لغة',
    gradesYouTeach: 'المستويات التي تقوم بتدريسها',
    confirmOver18: 'أؤكد أنني أكبر من 18 عاماً',
    emailFromAccount: 'البريد الإلكتروني من حسابك',
    // Step 2 - Photo
    uploadPhoto: 'تحميل الصورة',
    clickToUpload: 'انقر لتحميل الصورة',
    photoPreview: 'ستظهر معاينة الصورة هنا',
    removeChange: 'إزالة / تغيير',
    // Step 3 - Experience
    noExperience: 'ليس لدي أي خبرة',
    jobTitle: 'المسمى الوظيفي',
    organization: 'المؤسسة / المكان',
    currentlyWorkHere: 'أعمل هنا حالياً',
    from: 'من',
    to: 'إلى',
    month: 'الشهر',
    year: 'السنة',
    addMore: 'إضافة المزيد',
    // Step 4 - Description
    shortBio: 'نبذة قصيرة',
    generateWithAI: 'إنشاء بالذكاء الاصطناعي',
    generating: 'جاري الإنشاء...',
    words: 'كلمة',
    // Step 5 - Video
    chooseMethod: 'اختر الطريقة',
    pasteVideoLink: 'لصق رابط الفيديو',
    uploadVideo: 'تحميل الفيديو',
    videoLink: 'رابط الفيديو (يوتيوب أو فيميو)',
    uploadVideoFile: 'تحميل ملف الفيديو (mp4، حد أقصى 50 ميجابايت، 60 ثانية)',
    clickToUploadVideo: 'انقر لتحميل الفيديو',
    tipsForVideo: 'نصائح لفيديو تعريف رائع:',
    // Step 6 - Availability
    setAvailability: 'حدد جدول التوفر الأسبوعي الخاص بك. سيرى الطلاب متى تكون متاحاً.',
    addAnotherTimeslot: 'إضافة وقت آخر',
    // Step 7 - Pricing
    perHourPrivate: 'للساعة (خاص)',
    perHourGroup: 'للساعة (مجموعة)',
    bankingInfo: 'معلومات البنك',
    iban: 'رقم الآيبان',
    bankName: 'اسم البنك',
    accountHolderName: 'اسم صاحب الحساب',
    forReceivingPayments: 'لاستلام المدفوعات (اختياري)',
    // Step 8 - Places
    howDeliverLessons: 'كيف تفضل تقديم الدروس؟',
    onlineOnly: 'عبر الإنترنت فقط',
    onlineOnlyDesc: 'سأقوم بالتدريس عبر الإنترنت فقط (Zoom، Google Meet، إلخ)',
    faceToFaceOnly: 'وجهاً لوجه فقط',
    faceToFaceOnlyDesc: 'سأقوم بالتدريس شخصياً فقط (في المنزل، المكتبة، إلخ)',
    both: 'كلاهما',
    bothDesc: 'يمكنني التدريس عبر الإنترنت أو وجهاً لوجه (يختار الطالب)',
    preferredLocations: 'المواقع المفضلة للتدريس',
    addLocation: 'إضافة موقع',
    // Step 9 - Form
    interestedInCorporate: 'هل أنت مهتم بتدريس موظفي الشركات؟',
    corporateTrainingHelp: 'إذا أبرمنا عقداً مع شركة، هل ستكون مستعداً لتدريس موظفيها في مجال تخصصك؟',
    preferredCompanySize: 'ما حجم الشركات التي تفضل العمل معها؟',
    companySizeHelp: 'اختر أحجام الشركات التي تشعر بالراحة في تدريسها',
    trainingFormat: 'ما هي صيغة التدريب التي تفضلها؟',
    trainingFormatHelp: 'اختر صيغ التدريب التي تشعر بالراحة معها للتدريب المؤسسي',
    corporateAvailability: 'متى تكون متاحاً للتدريب المؤسسي؟',
    corporateAvailabilityHelp: 'اختر الأوقات المفضلة لديك لإجراء جلسات التدريب المؤسسي',
    helpMatchStudents: 'ساعدنا في مطابقتك مع الطلاب المناسبين',
    howPreferToTeach: 'كيف تفضل التدريس؟',
    whatLanguageLessons: 'ما اللغة التي ستستخدمها أثناء الدروس؟',
    whenTypicallyAvailable: 'متى تكون متاحاً عادة؟',
    // Review
    reviewYourProfile: 'مراجعة ملفك الشخصي',
    reviewSubtitle: 'يرجى مراجعة جميع معلوماتك قبل الإرسال. يمكنك تعديل أي قسم بالنقر على زر التعديل.',
    submitForReview: 'إرسال للمراجعة',
    // Review Section Titles
    information: 'المعلومات',
    aboutMe: 'نبذة عني',
    gradeLevels: 'المستويات',
    languages: 'اللغات',
    video: 'الفيديو',
    subjects: 'المواد',
    experience: 'الخبرة',
    pricing: 'التسعير',
    availability: 'التوفر',
    teachingLocations: 'مواقع التدريس',
    bankingInformation: 'معلومات البنك',
    preferredLocations: 'المواقع المفضلة:',
    location: 'موقع',
    // Errors
    required: 'مطلوب',
    validEmail: 'يرجى إدخال عنوان بريد إلكتروني صالح',
    atLeastOneLanguage: 'يجب إضافة لغة واحدة على الأقل',
    mustConfirmOver18: 'يجب تأكيد أنك أكبر من 18 عاماً',
    pleaseUploadPhoto: 'يرجى تحميل صورة الملف الشخصي',
    pleaseWriteBio: 'يرجى كتابة نبذة قصيرة',
    // Completion
    submissionReceived: 'تم استلام الطلب!',
    thankYouMessage: 'شكراً لإكمال تقديم ملف المعلم الخاص بك.',
    emailNotification: 'سيقوم فريق Taealam بمراجعة طلبك خلال 24 ساعة وسيتم الرد عليك في نفس الوقت.',
    goToDashboard: 'الذهاب إلى لوحة التحكم',
  }
};

export default function TeacherSignupWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStep = Math.min(Math.max(parseInt(searchParams.get('step') || '1', 10), 1), 9);
  
  // Language state
  const [language, setLanguage] = useState(() => {
    const savedLang = localStorage.getItem('taealam_language');
    return savedLang === 'ar' ? 'AR' : 'EN';
  });
  const isArabic = language === 'AR';

  // Helper functions for language persistence
  const setLanguageCookie = (lang) => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname.includes('localhost');
    const langValue = lang === 'AR' ? 'ar' : 'en';
    if (isLocalhost) {
      document.cookie = `taealam_language=${langValue}; path=/; max-age=31536000`;
    } else {
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      document.cookie = `taealam_language=${langValue}; path=/; domain=.${domain}; max-age=31536000`;
    }
  };

  const toggleLanguage = () => {
    const newLang = language === 'EN' ? 'AR' : 'EN';
    setLanguage(newLang);
    const langValue = newLang === 'AR' ? 'ar' : 'en';
    localStorage.setItem('taealam_language', langValue);
    setLanguageCookie(newLang);
    document.documentElement.dir = newLang === 'AR' ? 'rtl' : 'ltr';
    document.documentElement.lang = langValue;
  };

  // Update document direction on language change
  useEffect(() => {
    const langValue = language === 'AR' ? 'ar' : 'en';
    document.documentElement.dir = language === 'AR' ? 'rtl' : 'ltr';
    document.documentElement.lang = langValue;
  }, [language]);

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [wizardData, setWizardData] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'pending' | 'completed'

  const STEPS = STEP_LABELS[language].map((label, index) => ({
    number: index + 1,
    title: STEP_LABELS.EN[index],
    label: label
  }));

  // Load draft data on mount
  useEffect(() => {
    loadDraft();
  }, []);

  // Sync step with URL
  useEffect(() => {
    const urlStep = parseInt(searchParams.get('step') || '1', 10);
    if (urlStep !== currentStep && urlStep >= 1 && urlStep <= 9) {
      setCurrentStep(urlStep);
    }
  }, [searchParams]);

  // Update URL when step changes
  useEffect(() => {
    if (currentStep !== initialStep) {
      navigate(`/teacher/signup?step=${currentStep}`, { replace: true });
    }
  }, [currentStep, navigate, initialStep]);

  // Load draft from localStorage and Supabase
  const loadDraft = async () => {
    try {
      // Load from localStorage
      const savedDraft = localStorage.getItem('teacher_onboarding_draft');
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        setWizardData(parsed.data || {});
        setCompletedSteps(new Set(parsed.completedSteps || []));
        
        // If user was on a specific step, restore it
        if (parsed.lastStep && parsed.lastStep >= 1 && parsed.lastStep <= 9) {
          setCurrentStep(parsed.lastStep);
        }
      }

      // Load from Supabase if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: supabaseDraft } = await supabase
          .from('teacher_onboarding_draft')
          .select('*')
          .eq('teacher_id', user.id)
          .single();

        if (supabaseDraft && supabaseDraft.draft_data) {
          setWizardData(supabaseDraft.draft_data);
          setCompletedSteps(new Set(supabaseDraft.completed_steps || []));
          if (supabaseDraft.last_step) {
            setCurrentStep(supabaseDraft.last_step);
          }
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  };

  // Save draft to localStorage and Supabase
  const saveDraft = async (stepData = {}, markStepComplete = false) => {
    try {
      const updatedData = { ...wizardData, ...stepData };
      const updatedCompleted = new Set(completedSteps);
      
      if (markStepComplete) {
        updatedCompleted.add(currentStep);
      }

      const draftPayload = {
        data: updatedData,
        completedSteps: Array.from(updatedCompleted),
        lastStep: currentStep,
        updatedAt: new Date().toISOString()
      };

      // Save to localStorage
      localStorage.setItem('teacher_onboarding_draft', JSON.stringify(draftPayload));

      // Save to Supabase if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase
          .from('teacher_onboarding_draft')
          .upsert({
            teacher_id: user.id,
            draft_data: updatedData,
            completed_steps: Array.from(updatedCompleted),
            last_step: currentStep,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'teacher_id'
          });

        if (error) {
          console.error('Error saving to Supabase:', error);
        }
      }

      setWizardData(updatedData);
      setCompletedSteps(updatedCompleted);
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  };

  // Validation functions for each step
  const validateStep = (stepNumber, data = wizardData) => {
    switch (stepNumber) {
        case 1: // About
          if (!data.full_name || data.full_name.trim() === '') return false;
          if (!data.email || data.email.trim() === '' || !/\S+@\S+\.\S+/.test(data.email)) return false;
          if (!data.phone || data.phone.trim() === '') return false;
          if (!data.date_of_birth_day || !data.date_of_birth_month || !data.date_of_birth_year) return false;
          // Validate age (must be 18 or older)
          if (data.date_of_birth_day && data.date_of_birth_month && data.date_of_birth_year) {
            const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const monthIndex = MONTHS.indexOf(data.date_of_birth_month);
            if (monthIndex !== -1) {
              const birthDate = new Date(parseInt(data.date_of_birth_year), monthIndex, parseInt(data.date_of_birth_day));
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
              if (age < 18) return false;
            }
          }
          if (!data.gender || data.gender === '') return false;
          // Support both old (subject) and new (subjects) format
          const subjects = data.subjects || (data.subject ? [data.subject] : []);
          if (!subjects || subjects.length === 0) return false;
          if (subjects.length > 3) return false;
          if (!data.languages || data.languages.length === 0) return false;
          if (!data.grade_levels || data.grade_levels.length === 0) return false;
          if (!data.over_18_confirmed) return false;
          return true;
      
      case 2: // Photo
        if (!data.photo_url) return false;
        return true;
      
      case 3: // Experience
        // Experience is valid if either experience_none is true OR experiences array has at least one valid entry
        if (data.experience_none === true) return true;
        if (data.experiences && data.experiences.length > 0) {
          // Check if at least one experience has required fields
          return data.experiences.some(exp => 
            exp.job_title && exp.job_title.trim() !== '' &&
            exp.organization && exp.organization.trim() !== '' &&
            exp.from_month && exp.from_year
          );
        }
        return false;
      
      case 4: // Description
        if (!data.short_bio || data.short_bio.trim() === '') return false;
        // Check word count (max 23 words)
        const wordCount = data.short_bio.trim().split(/\s+/).filter(Boolean).length;
        if (wordCount > 23) return false;
        return true;
      
      case 5: // Video (optional)
        return true; // Always valid as it's optional
      
      case 6: // Availability
        if (!data.availability_schedule || typeof data.availability_schedule !== 'object') {
          return false;
        }
        // Check if at least one day has availability (either allDay or has slots)
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        return days.some(day => {
          const dayData = data.availability_schedule[day];
          if (!dayData) return false;
          // Valid if allDay is true OR has slots with from/to times
          if (dayData.allDay === true) return true;
          if (dayData.slots && Array.isArray(dayData.slots) && dayData.slots.length > 0) {
            return dayData.slots.some(slot => slot.from && slot.to);
          }
          return false;
        });
      
      case 7: // Places
        if (!data.lesson_format_preference) return false;
        // Locations are now optional - no validation needed
        return true;
      
      case 8: // Pricing
        // Check if "both" option is selected (separate online/face-to-face pricing)
        if (data.lesson_format_preference === 'both') {
          if (!data.pricing_private_online_per_hour || data.pricing_private_online_per_hour.trim() === '') return false;
          if (!data.pricing_group_online_per_hour || data.pricing_group_online_per_hour.trim() === '') return false;
          if (!data.pricing_private_face_to_face_per_hour || data.pricing_private_face_to_face_per_hour.trim() === '') return false;
          if (!data.pricing_group_face_to_face_per_hour || data.pricing_group_face_to_face_per_hour.trim() === '') return false;
        } else {
          // Single pricing (online only or face-to-face only)
          if (!data.pricing_private_per_hour || data.pricing_private_per_hour.trim() === '') return false;
          if (!data.pricing_group_per_hour || data.pricing_group_per_hour.trim() === '') return false;
        }
        // Banking Information is now required
        if (!data.iban || data.iban.trim() === '') return false;
        if (!data.bank_name || data.bank_name === '') return false;
        if (!data.account_holder_name || data.account_holder_name.trim() === '') return false;
        return true;
      
      case 9: // Review
        // Review step doesn't need validation - it's the final step
        return true;
      
      default:
        return false;
    }
  };

  // Check if all steps up to a target step are completed
  const canNavigateToStep = (targetStep) => {
    // Always allow going back
    if (targetStep < currentStep) return true;
    
    // Can't go to current step (no-op)
    if (targetStep === currentStep) return true;
    
    // For forward navigation, validate ALL steps from 1 to target step
    if (targetStep > currentStep) {
      // Validate all steps from 1 up to (but not including) target step
      // This ensures users can't skip any required steps
      for (let step = 1; step < targetStep; step++) {
        // Skip optional step 5 (Video)
        if (step === 5) continue;
        
        // Check if step is completed AND valid
        if (!completedSteps.has(step) || !validateStep(step)) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Handle step navigation with validation
  const goToStep = (step) => {
    if (step < 1 || step > 9) return;
    
    // Check if navigation is allowed
    if (!canNavigateToStep(step)) {
      // Show error message
      const t = wizardTranslations[language];
      const currentStepLabel = STEP_LABELS[language][currentStep - 1];
      const errorMsg = isArabic 
        ? `يرجى إكمال جميع الحقول المطلوبة في "${currentStepLabel}" قبل الانتقال إلى الخطوة التالية.`
        : `Please complete all required fields in "${currentStepLabel}" before proceeding to the next step.`;
      alert(errorMsg);
      return;
    }
    
    setCurrentStep(step);
    navigate(`/teacher/signup?step=${step}`, { replace: true });
  };

  // Handle next step
  const handleNext = async (stepData = {}) => {
    // Merge step data with wizard data for validation
    const updatedData = { ...wizardData, ...stepData };
    
    // Validate current step before proceeding
    if (!validateStep(currentStep, updatedData)) {
      const t = wizardTranslations[language];
      const currentStepLabel = STEP_LABELS[language][currentStep - 1];
      const errorMsg = isArabic 
        ? `يرجى إكمال جميع الحقول المطلوبة في "${currentStepLabel}" قبل الانتقال إلى الخطوة التالية.`
        : `Please complete all required fields in "${currentStepLabel}" before proceeding to the next step.`;
      alert(errorMsg);
      return;
    }
    
    await saveDraft(stepData, true);
    
    if (currentStep < 9) {
      goToStep(currentStep + 1);
    }
    // Step 9 (Review) handles submission via handleFinish
  };

  // Handle back
  const handleBack = async (stepData = {}) => {
    await saveDraft(stepData);
    
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  };

  // Handle finish - submit for review
  const handleFinish = async (finalStepData = {}) => {
    try {
      setIsSubmitting(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const finalData = { ...wizardData };

      // Mark as submitted for review
      const { error: submitError } = await supabase
        .from('teacher_onboarding_submissions')
        .insert({
          teacher_id: user.id,
          submission_data: finalData,
          status: 'pending', // 'pending' | 'approved' | 'rejected'
          submitted_at: new Date().toISOString()
        });

      if (submitError) throw submitError;

      // Clear draft
      localStorage.removeItem('teacher_onboarding_draft');
      await supabase
        .from('teacher_onboarding_draft')
        .delete()
        .eq('teacher_id', user.id);

      setSubmissionStatus('completed');
    } catch (error) {
      console.error('Error submitting:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step
  const renderStep = () => {
    const translations = wizardTranslations[language];
    const stepProps = {
      wizardData,
      onNext: handleNext,
      onBack: handleBack,
      onSave: saveDraft,
      isSubmitting,
      isArabic,
      language,
      translations
    };

    switch (currentStep) {
      case 1:
        return <Step1About {...stepProps} />;
      case 2:
        return <Step2Photo {...stepProps} />;
      case 3:
        return <Step3Experience {...stepProps} />;
      case 4:
        return <Step4Description {...stepProps} />;
      case 5:
        return <Step5Video {...stepProps} />;
      case 6:
        return <Step6Availability {...stepProps} />;
      case 7:
        return <Step8Places {...stepProps} />;
      case 8:
        return <Step7Pricing {...stepProps} />;
      case 9:
        return <Step10Review {...stepProps} onSubmit={handleFinish} />;
      default:
        return <Step1About {...stepProps} />;
    }
  };

  if (submissionStatus === 'completed') {
    return <WizardCompletion isArabic={isArabic} language={language} />;
  }

  return (
    <div className="wizard-container" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="wizard-card">
        {/* Language Toggle Button - Top Right */}
        <div style={{
          position: 'absolute',
          top: '32px',
          [isArabic ? 'left' : 'right']: '40px',
          zIndex: 10
        }}>
          <button
            onClick={toggleLanguage}
            aria-label="Toggle language"
            style={{
              padding: '8px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              background: 'white',
              color: '#374151',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontFamily: isArabic ? "'Noto Sans Arabic', sans-serif" : 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1E293B';
              e.currentTarget.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.background = 'white';
            }}
          >
            <span>{isArabic ? 'EN' : 'العربية'}</span>
          </button>
        </div>

        {/* Stepper - Positioned below language button */}
        <div className="wizard-stepper" style={{ marginTop: '60px' }}>
          {STEPS.map((step, index) => {
            const isClickable = canNavigateToStep(step.number);
            
            return (
              <React.Fragment key={step.number}>
                <div
                  className={`stepper-item ${currentStep === step.number ? 'active' : ''} ${completedSteps.has(step.number) ? 'completed' : ''} ${!isClickable ? 'disabled' : ''}`}
                  onClick={() => {
                    if (isClickable) {
                      goToStep(step.number);
                    }
                  }}
                  style={{
                    cursor: isClickable ? 'pointer' : 'not-allowed',
                    opacity: isClickable ? 1 : 0.5
                  }}
                  title={!isClickable ? (isArabic ? 'يرجى إكمال الخطوة الحالية أولاً' : 'Please complete the current step first') : ''}
                >
                  <div className="stepper-number">
                    {completedSteps.has(step.number) ? (
                      <FaCheck style={{ fontSize: '14px' }} />
                    ) : step.number}
                  </div>
                  <span className="stepper-label">{step.label}</span>
                </div>
                {index < STEPS.length - 1 && <div className="stepper-line" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="wizard-content">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}